// Do it at once, offer "Ongedaan maken" (spec §5.7): what can be undone never asks first.
// Shown by <UndoToast /> (PrimeVue Toast, group 'undo'); one undo at a time, a new action replaces the offer.
import { useToast } from 'primevue/usetoast';

export const UNDO_GROUP = 'undo';

type Offer = { message: string; undo: () => unknown; left: number; paused: boolean };
// Module state, shared by every useUndo() and the one UndoToast that shows it
export const undoOffer = ref<Offer | null>(null);
let timer: ReturnType<typeof setInterval> | undefined;

export function useUndo() {
  const toast = useToast();
  const { t } = useI18n();

  const clear = () => {
    clearInterval(timer);
    undoOffer.value = null;
    toast.removeGroup(UNDO_GROUP);
  };

  /** Runs `do`; an error is thrown to the caller (shown where it happened), never offered for undo. */
  async function run<T>(o: { do: () => T | Promise<T>; undo: () => unknown; message: string; seconds?: number }): Promise<T> {
    const result = await o.do();
    clear();
    undoOffer.value = { message: o.message, undo: o.undo, left: o.seconds ?? 8, paused: false };
    toast.add({ group: UNDO_GROUP, severity: 'contrast', closable: false });
    // Our own clock instead of the toast's life, so the countdown shown is the real one and pauses on hover and focus
    timer = setInterval(() => {
      const offer = undoOffer.value;
      if (!offer || offer.paused) return;
      if (--offer.left <= 0) clear();
    }, 1000);
    return result;
  }

  async function undo() {
    const offer = undoOffer.value;
    if (!offer) return;
    clear();
    try {
      await offer.undo();
      toast.add({ group: UNDO_GROUP, severity: 'contrast', detail: t('undo_undone'), life: 2000, closable: false });
    } catch (e) {
      console.error(e);
      toast.add({ group: UNDO_GROUP, severity: 'error', detail: t('undo_failed'), life: 6000 });
    }
  }

  return { run, undo, dismiss: clear };
}
